import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AccountSearchRequest, AccountSearchResponse, CreateMandantRequest, DefaultResponse, EditMandantRequest, ListMandantsResponse, MandantIdRequest } from '@reflact/reflactportal';
import { ObjectId } from 'bson';
import { LoginService } from './login.service';
import { firstValueFrom } from 'rxjs';
import { Mandant } from '../types/mandant.types';

interface GetMyMandantResponse extends DefaultResponse {
  mandant: Mandant;
}

@Injectable({
  providedIn: 'root'
})
export class MandantService {
  public mandants: Mandant[] = [];

  constructor(public http: HttpClient, public loginService: LoginService) { }

  public async getMandants(): Promise<Mandant[]> {
    const res = await firstValueFrom(this.http.get<ListMandantsResponse & { mandants: Mandant[] }>("/api/mandant/list"));
    if (res.status == "ok") {
      this.mandants = res.mandants;
      return res.mandants;
    }
    return [];
  }

  /** Der aktuell ausgewählte Mandant (Kachel-Pool `tiles` + tatsächlich aktivierte `activeTiles`)
   * — im Gegensatz zu `getMandants()` auch für Mandanten-Admins (nicht nur reflectadmin)
   * zugänglich, siehe `/api/mandant/mine`. */
  public async getMyMandant(): Promise<Mandant | null> {
    const res = await firstValueFrom(this.http.get<GetMyMandantResponse>("/api/mandant/mine"));
    if (res.status == "ok") {
      return res.mandant;
    }
    return null;
  }

  /** Startseiten-Meldung (HTML) des aktuell ausgewählten Mandanten. */
  public async getHomeMessage(): Promise<string> {
    const res = await firstValueFrom(this.http.get<DefaultResponse & { homeMessage?: string }>("/api/mandant/homeMessage"));
    return res.status == "ok" ? (res.homeMessage ?? "") : "";
  }

  /** Nur für Mandanten-Admins; leerer String entfernt die Meldung. */
  public async setHomeMessage(homeMessage: string): Promise<DefaultResponse> {
    return await this.postSafe<DefaultResponse>("/api/mandant/setHomeMessage", { homeMessage });
  }

  /** Zweite Stufe des zweistufigen Kachel-Modells: wählt aus dem vom reflect-admin freigegebenen
   * Pool (`tiles`), welche Kacheln für die Nutzer des aktuellen Mandanten tatsächlich aktiv sein
   * sollen — von jedem Mandanten-Admin (Organization Role "admin") nutzbar. */
  public async setActiveTiles(activeTiles: string[]): Promise<DefaultResponse> {
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/mandant/setActiveTiles", { activeTiles }));
    return res;
  }

  /** Dialog „Kacheln verwalten“ in einem Schritt (#122): Aktivierung für alle plus individuelle
   * Freigaben der Pool-Kacheln (`selectedUsersByTile`: Kachel-Id → Nutzer-Ids). */
  public async setTileSharing(activeTiles: string[], selectedUsersByTile: Record<string, string[]>): Promise<DefaultResponse> {
    return await firstValueFrom(this.http.post<DefaultResponse>("/api/mandant/setTileSharing", { activeTiles, selectedUsersByTile }));
  }

  public async editManant(mandantId: ObjectId, name: string, tiles: ObjectId[], internal = false): Promise<DefaultResponse> {
    const req: EditMandantRequest & { internal: boolean } = {
      "mandantId": mandantId,
      "name": name,
      "tiles": tiles,
      "internal": internal
    }
    return await this.postSafe<DefaultResponse>("/api/mandant/edit", req);
  }

  public async createMandant(name: string, short: string, tiles: ObjectId[], internal = false): Promise<DefaultResponse> {
    const req: CreateMandantRequest & { internal: boolean } = {
      "name": name,
      "short": short,
      "tiles": tiles,
      "internal": internal
    }
    return await this.postSafe<DefaultResponse>("/api/mandant/create", req);
  }

  /** Ob der aktuell ausgewählte Mandant intern (kein Kunde) ist — für das Logo-Badge. */
  public async isCurrentMandantInternal(): Promise<boolean> {
    const res = await firstValueFrom(this.http.get<DefaultResponse & { internal?: boolean }>("/api/mandant/current"));
    return res.status == "ok" && res.internal === true;
  }

  public async deleteMandant(mandantId: ObjectId): Promise<DefaultResponse> {
    const req: MandantIdRequest = {
      "mandantId": mandantId
    }
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/mandant/delete", req));
    return res;
  }

  public findMandant(id: ObjectId) {
    return this.mandants.find(m => { return m._id.toString() === id.toString() });
  }

  public async searchAccount(search: string) {
    const req: AccountSearchRequest = {
      search
    }
    return await firstValueFrom(this.http.post<AccountSearchResponse>("/api/myreflact/accountsearch", req));
  }

  /** `firstValueFrom` wirft bei einer Nicht-2xx-Antwort (z.B. 409 bei doppeltem Kürzel) statt das
   * Ergebnis aufzulösen - ohne dieses Auffangen bliebe der Fehler ungefangen und der Aufrufer
   * bekommt weder Erfolg noch Fehlermeldung angezeigt ("das Formular tut einfach nichts"). */
  private async postSafe<T extends DefaultResponse>(url: string, body: unknown): Promise<T> {
    try {
      return await firstValueFrom(this.http.post<T>(url, body));
    } catch (error) {
      const message = error instanceof HttpErrorResponse ? error.error?.message : undefined;
      return { status: 'error', message: message ?? 'Die Anfrage ist fehlgeschlagen.' } as T;
    }
  }
}
